// src/pages/CreateLeague.js
import React, { useEffect, useState, useCallback } from "react";
import { db } from "../lib/firebase";
import { collection, doc, setDoc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import Layout from "../central/Layout";
import PageLoader from "../components/PageLoader";
import LoadingOverlay from "../central/LoadingOverlay";
import Button from "../central/Button";
import Dropdown from "../central/Dropdown";
import Panel from "../central/Panel";
import PlayerRatingsStep from "../components/PlayerRatingsStep";
import { v4 as uuidv4 } from "uuid";
import { getLeagueConfig } from "../utils/leagueConfig";
import { logError } from "../utils/logError";
import { logEvent } from "../utils/EventLogger";
import { useToast } from "../context/ToastContext";

// ------------- Draft helpers (per-user key) -------------
const DRAFT_KEY = (uid) => `createLeagueDraft_v1:${uid}`;

function loadDraft(uid) {
  if (!uid) return null;
  try {
    const raw = localStorage.getItem(DRAFT_KEY(uid));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}
function saveDraft(uid, draft) {
  if (!uid) return;
  localStorage.setItem(DRAFT_KEY(uid), JSON.stringify(draft));
}
function clearDraft(uid) {
  if (!uid) return;
  localStorage.removeItem(DRAFT_KEY(uid));
}
// --------------------------------------------------------

const DEFAULT_PRICES = { DEF: 5.0, MID: 5.5, FWD: 6.0 };
const RATING_ADJ = { 1: -1.0, 2: -0.5, 3: 0.0, 4: 0.5, 5: 1.0 };

const computePrice = (pos, rating) =>
  Number(((DEFAULT_PRICES[pos] ?? 0) + (RATING_ADJ[rating] ?? 0)).toFixed(1));

const buildEmptyPlayers = (count = 1) =>
  Array.from({ length: Math.max(1, count) }, () => ({
    id: uuidv4(),
    name: "",
    position: "MID",
    rating: 3,
    price: computePrice("MID", 3),
  }));

export default function CreateLeague() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [step, setStep] = useState(1);

  // Step 1
  const [leagueName, setLeagueName] = useState("");
  const [teamName, setTeamName] = useState("");
  const [leagueType, setLeagueType] = useState("5");
  // New league settings
  const [deadline, setDeadline] = useState("");
  const [matchDay, setMatchDay] = useState("");
  const [timeOfDay, setTimeOfDay] = useState("");
  // State flags for dropdown selection
  const [isMatchDaySelected, setIsMatchDaySelected] = useState(false);
  const [isTimeOfDaySelected, setIsTimeOfDaySelected] = useState(false);
  // These are stored for Firestore, not shown in UI
  // Always compute from leagueType in useEffect
  const [maxmaxFreeTransfers, setMaxmaxFreeTransfers] = useState(0);
  const [transferPenaltyPoints, setTransferPenaltyPoints] = useState(0);

  // Step 2 + 3
  const [players, setPlayers] = useState([
    {
      id: uuidv4(),
      name: "",
      position: "MID",
      rating: 3,
      price: computePrice("MID", 3),
    },
  ]);

  // Step 4 (chips & review)
  const [chipsEnabled, setChipsEnabled] = useState(() => {
    const config = getLeagueConfig(leagueType);
    return config?.chipsEnabled ?? true;
  });

  const [submitting, setSubmitting] = useState(false);
  const [successModal, setSuccessModal] = useState(null); // { leagueName, leagueCode, leagueID }
  const [copiedCode, setCopiedCode] = useState(false);
  const [copiedInvite, setCopiedInvite] = useState(false);

  // Always use string leagueType to pass to getLeagueConfig
  const leagueConfig = getLeagueConfig(String(leagueType)) || {};
  const requiredPlayerCount = leagueConfig?.minPlayers ?? 0;

  // Load draft on mount
  useEffect(() => {
    if (!currentUser) return;
    const draft = loadDraft(currentUser.uid);
    if (draft) {
      setStep(draft.step ?? 1);
      setLeagueName(draft.leagueName ?? "");
      setTeamName(draft.teamName ?? "");
      setLeagueType(draft.leagueType ?? "5");
      setPlayers(
        Array.isArray(draft.players) && draft.players.length > 0
          ? draft.players.map((p) => {
              const pos = p.position || "MID";
              const rating =
                typeof p.rating === "number" && p.rating >= 1 && p.rating <= 5
                  ? p.rating
                  : 3;
              const price =
                typeof p.price === "number"
                  ? p.price
                  : computePrice(pos, rating);
              return {
                id: p.id || uuidv4(),
                name: p.name || "",
                position: pos,
                rating,
                price,
              };
            })
          : [
              {
                id: uuidv4(),
                name: "",
                position: "MID",
                rating: 3,
                price: computePrice("MID", 3),
              },
            ]
      );
      const config = getLeagueConfig(draft.leagueType ?? "5");
      setChipsEnabled(
        typeof draft.chipsEnabled === "boolean" ? draft.chipsEnabled : config.chipsEnabled
      );
      // Restore new settings if present in draft
      setDeadline(draft.deadline ?? "");
      setMatchDay(draft.matchDay ?? "");
      setTimeOfDay(draft.timeOfDay ?? "");
      setIsMatchDaySelected(!!draft.matchDay);
      setIsTimeOfDaySelected(!!draft.timeOfDay);
      // Always use config for maxmaxFreeTransfers and transferPenaltyPoints
      setMaxmaxFreeTransfers(config?.maxFreeTransfers ?? 2);
      setTransferPenaltyPoints(config?.transferPenaltyPoints ?? 5);
    }
  }, [currentUser]);

  // Autosave draft
  useEffect(() => {
    if (!currentUser) return;
    // Always use config for maxmaxFreeTransfers and transferPenaltyPoints
    const config = getLeagueConfig(String(leagueType));
    saveDraft(currentUser.uid, {
      step,
      leagueName,
      teamName,
      leagueType,
      players,
      chipsEnabled,
      deadline,
      matchDay,
      timeOfDay,
      maxmaxFreeTransfers: config?.maxFreeTransfers ?? 2,
      transferPenaltyPoints: config?.transferPenaltyPoints ?? 5,
    });
  }, [
    currentUser,
    step,
    leagueName,
    teamName,
    leagueType,
    players,
    chipsEnabled,
    deadline,
    matchDay,
    timeOfDay,
  ]);

  // Whenever leagueType changes, update maxmaxFreeTransfers and transferPenaltyPoints from config
  useEffect(() => {
    const config = getLeagueConfig(String(leagueType));
    if (config) {
      setMaxmaxFreeTransfers(config.maxFreeTransfers);
      setTransferPenaltyPoints(config.transferPenaltyPoints);
    }
  }, [leagueType]);

  // Step 2: auto-open correct number of empty player rows
  useEffect(() => {
    if (step !== 2) return;

    const minPlayers = getLeagueConfig(String(leagueType))?.minPlayers ?? 1;

    setPlayers((prev) => {
      if (prev.length >= minPlayers) return prev;
      return [...prev, ...buildEmptyPlayers(minPlayers - prev.length)];
    });
  }, [step, leagueType]);

  // ---------- Deadline auto-calculation ----------
  // Days of week to index (0=Sunday, ... 6=Saturday)
  const daysOfWeek = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];
  // Helper to get next date for a given day of week
  const getNextDateForDay = useCallback(
    function(dayName) {
      if (!dayName) return null;
      const today = new Date();
      const dayIdx = daysOfWeek.findIndex(
        (d) => d.toLowerCase() === dayName.toLowerCase()
      );
      if (dayIdx === -1) return null;
      // JS: Sunday=0 ... Saturday=6, so we map daysOfWeek[6]=Sunday=6
      // But we want Monday=0, so shift accordingly
      // Actually, let's map Monday=1, ..., Sunday=0
      // We'll use: [1,2,3,4,5,6,0]
      const jsDayIdx = (dayIdx + 1) % 7;
      const diff = (jsDayIdx - today.getDay() + 7) % 7 || 7;
      const result = new Date(today);
      result.setDate(today.getDate() + diff);
      result.setHours(0, 0, 0, 0);
      return result;
    },
    [daysOfWeek]
  );
  // Deadline auto-calc
  useEffect(() => {
    if (!matchDay || !timeOfDay) return;
    const matchDate = getNextDateForDay(matchDay);
    if (!matchDate) return;
    let deadlineDate = new Date(matchDate);
    if (timeOfDay === "Evening") {
      // Deadline is same day at 14:00
      deadlineDate.setHours(14, 0, 0, 0);
    } else if (timeOfDay === "Daytime") {
      // Deadline is same day at 9:00
      deadlineDate.setHours(9, 0, 0, 0);
    }
    // Format for datetime-local input: yyyy-MM-ddTHH:mm
    const pad = (n) => n.toString().padStart(2, "0");
    const fmt = `${deadlineDate.getFullYear()}-${pad(deadlineDate.getMonth() + 1)}-${pad(deadlineDate.getDate())}T${pad(deadlineDate.getHours())}:${pad(deadlineDate.getMinutes())}`;
    setDeadline(fmt);
  }, [matchDay, timeOfDay, getNextDateForDay]);

  // ---------- handlers ----------
  const handleAddPlayerRow = () =>
    setPlayers((prev) => [
      ...prev,
      {
        id: uuidv4(),
        name: "",
        position: "MID",
        rating: 3,
        price: computePrice("MID", 3),
      },
    ]);

  const handleRemovePlayerRow = (idx) =>
    setPlayers((prev) => prev.filter((_, i) => i !== idx));

  const handlePlayerField = (idx, field, value) => {
    setPlayers((prev) => {
      const next = prev.map((p, i) => (i === idx ? { ...p } : p));

      if (field === "rating") {
        const r = parseInt(value, 10);
        next[idx].rating = Number.isNaN(r) ? 3 : Math.min(5, Math.max(1, r));
      } else {
        next[idx][field] = value;
      }

      const pos = next[idx].position || "MID";
      const rating =
        typeof next[idx].rating === "number" ? next[idx].rating : 3;
      next[idx].price = computePrice(pos, rating);

      return next;
    });
  };

  // ---------- validation ----------
  const canProceedStep1 =
    leagueName.trim().length > 0 &&
    teamName.trim().length > 0 &&
    !!matchDay &&
    !!timeOfDay;

  const step1Errors = [];
  if (!leagueName.trim()) step1Errors.push("Please enter a league name.");
  if (!teamName.trim()) step1Errors.push("Please enter your team name.");
  if (!matchDay) step1Errors.push("Please select a match day.");
  if (!timeOfDay) step1Errors.push("Please select a match time.");

  const showStep1Errors =
    step === 1 &&
    step1Errors.length > 0 &&
    (leagueName.trim() || teamName.trim() || matchDay || timeOfDay);

  const validPlayersByName = players.filter((p) => p.name?.trim());

  const normalizedNames = validPlayersByName
    .map((p) => p.name.trim().toLowerCase())
    .filter(Boolean);

  const nameCounts = normalizedNames.reduce((acc, n) => {
    acc[n] = (acc[n] || 0) + 1;
    return acc;
  }, {});

  const duplicateNameKeys = Object.keys(nameCounts).filter((k) => nameCounts[k] > 1);
  const hasDuplicateNames = duplicateNameKeys.length > 0;

  const canProceedStep2 =
    validPlayersByName.length >= requiredPlayerCount &&
    !hasDuplicateNames;

  const step2Errors = [];
  if (validPlayersByName.length < requiredPlayerCount) {
    step2Errors.push(`Please add at least ${requiredPlayerCount} players.`);
  }
  if (hasDuplicateNames) {
    // Display duplicates in a friendly format (title case-ish)
    const display = duplicateNameKeys
      .map((k) => k.replace(/\b\w/g, (c) => c.toUpperCase()))
      .join(", ");
    step2Errors.push(`Player names must be unique (duplicates: ${display}).`);
  }

  const showStep2Errors = step === 2 && step2Errors.length > 0;

  const positionCounts = players.reduce(
    (acc, p) => {
      if (p.position === "DEF") acc.DEF += 1;
      if (p.position === "MID") acc.MID += 1;
      if (p.position === "FWD") acc.FWD += 1;
      return acc;
    },
    { DEF: 0, MID: 0, FWD: 0 }
  );

  const hasAllPositions =
    positionCounts.DEF > 0 &&
    positionCounts.MID > 0 &&
    positionCounts.FWD > 0;

  const canProceedStep3 =
    players.length > 0 &&
    players.every(
      (p) =>
        p.name?.trim() &&
        ["DEF", "MID", "FWD"].includes(p.position || "MID") &&
        typeof p.rating === "number" &&
        p.rating >= 1 &&
        p.rating <= 5
    ) &&
    hasAllPositions;

  const step3Errors = [];
  if (!hasAllPositions) {
    const missing = [];
    if (positionCounts.DEF === 0) missing.push("DEF");
    if (positionCounts.MID === 0) missing.push("MID");
    if (positionCounts.FWD === 0) missing.push("FWD");
    step3Errors.push(`Please include at least one player for each position (${missing.join(", ")}).`);
  }

  const showStep3Errors = step === 3 && step3Errors.length > 0;

  const nextStep = () => {
    setStep((s) => {
      if (s === 2) {
        // When leaving step 2, drop any blank rows.
        setPlayers((prev) => prev.filter((p) => p.name?.trim()));
      }
      return s + 1;
    });
  };
  const prevStep = () => setStep((s) => Math.max(1, s - 1));

  // ---------- Firestore write (nested under the league) ----------
  const handleCreateLeague = async () => {
    const config = getLeagueConfig(String(leagueType));
    if (!currentUser || submitting) return;

    // Don't proceed unless positions/ratings are set
    if (!canProceedStep3) {
      showToast("Please complete positions and ratings for all players.", "error");
      return;
    }

    let resolvedUserName = "";
    try {
      setSubmitting(true);
      // Resolve userName from Firestore user doc (fallback to email prefix)
      try {
        const userDocRef = doc(db, "users", currentUser.uid);
        const userDocSnap = await getDoc(userDocRef);
        if (userDocSnap.exists()) {
          const data = userDocSnap.data();
          resolvedUserName =
            data?.displayName ||
            currentUser?.email?.split("@")[0] ||
            "";
        } else {
          resolvedUserName =
            currentUser?.email?.split("@")[0] ||
            "";
        }
      } catch {
        resolvedUserName =
          currentUser?.email?.split("@")[0] ||
          "";
      }

      // 1) Create the league document (top-level)
      const leagueRef = doc(collection(db, "leagues"));
      const generatedLeagueCode = Math.random().toString(36).substr(2, 6).toUpperCase();
      await setDoc(leagueRef, {
        name: leagueName.trim(),
        code: generatedLeagueCode,
        createdBy: currentUser.uid,
        leagueType: String(leagueType),
        chipsEnabled: chipsEnabled,
        matchDay,
        timeOfDay,
        initialDeadline: deadline ? new Date(deadline) : null,
        maxFreeTransfers: config.maxFreeTransfers,
        transferPenaltyPoints: config.transferPenaltyPoints,
        currentGW: "GW1",
        createdAt: new Date(),
        currentSeasonID: "S1",
        availabilityEnabled: true,
      });

      // --- 1. Create initial GW1 document ---
      const gw1Ref = doc(collection(db, "leagues", leagueRef.id, "seasons", "S1", "gameweeks"), "GW1");
      await setDoc(gw1Ref, {
        id: "GW1",
        number: 1,
        start: serverTimestamp(),
        deadline: deadline ? new Date(deadline) : null,
        createdAt: serverTimestamp(),
        isCurrent: true,
        phase: "pick",
        matchDay,
        timeOfDay,
      });

      // 2. Seed all players into the league's player pool
      let attemptedSeedCount = 0;
      let seededCount = 0;
      let seedFailCount = 0;
      const playersCol = collection(db, "leagues", leagueRef.id, "seasons", "S1", "players");
      for (const p of players) {
        attemptedSeedCount += 1;
        if (!p.name?.trim()) continue;
        try {
          const basePrice = Number(p.price);
          await setDoc(doc(playersCol, p.id), {
            id: p.id,
            name: p.name.trim(),
            position: p.position,
            rating: p.rating,
            originalPrice: basePrice,
            currentPrice: basePrice,
            priceHistory: { GW1: basePrice },
            priceAccumulator: 0,
          });
          seededCount += 1;
        } catch (e) {
          seedFailCount += 1;
          console.error("Failed to seed player", p, e);
          await logError({
            leagueID: leagueRef.id,
            userID: currentUser?.uid,
            action: "handleCreateLeague_seedPlayer",
            payload: { player: p },
            error: e
          });
        }
      }

      try {
        await logEvent({
          eventType: "player_seeded",
          leagueID: leagueRef.id,
          userID: currentUser.uid,
          userName: resolvedUserName,
          role: "admin",
          source: "create_league",
          metadata: {
            seasonID: "S1",
            attemptedSeedCount,
            seededCount,
            seedFailCount,
          },
        });
      } catch (e) {
        // never block league creation for analytics
        console.warn("[analytics] player_seeded failed", e);
      }

      // 3. Create the admin's team document
      const teamsCol = collection(db, "leagues", leagueRef.id, "seasons", "S1", "teams");
      const teamRef = doc(teamsCol);
      try {
        await setDoc(teamRef, {
          managerID: currentUser.uid,
          leagueID: leagueRef.id,
          name: teamName.trim(),
          createdAt: serverTimestamp(),
          // Explicitly set startGW to "GW1" for league-level teams doc
          startGW: "GW1",
          leagueType: String(leagueType),
          transfersMade: { GW1: 0 },
          transfersFree: { GW1: "unlimited" },
          chipsUsed: {},
          captains: {},
          selections: {},
        });
      } catch (e) {
        console.error("Failed to create admin team", e);
        await logError({
          leagueID: leagueRef.id,
          userID: currentUser?.uid,
          action: "handleCreateLeague_createTeam",
          payload: {},
          error: e
        });
      }

      // 3b. Seed an initial GW1 team snapshot for the admin
      try {
        const gw1TeamsCol = collection(db, "leagues", leagueRef.id, "seasons", "S1", "gameweeks", "GW1", "teams");
        const openingBudget = (config && typeof config.budget === "number") ? config.budget : null;
        await setDoc(doc(gw1TeamsCol, teamRef.id), {
          leagueID: leagueRef.id,
          teamID: teamRef.id,
          managerID: currentUser.uid,
          selections: {},                  // user will fill on first save
          squadMeta: {},
          captains: {},
          chipsUsed: {},
          transfersMade: 0,
          bankStart: openingBudget != null ? { GW1: openingBudget } : {},
          currentBudget: openingBudget != null ? { GW1: Number(openingBudget) } : {},
          lastSavedAt: serverTimestamp(),
        });
      } catch (e) {
        console.error("Failed to seed initial GW1 team snapshot", e);
        await logError({
          leagueID: leagueRef.id,
          userID: currentUser?.uid,
          action: "handleCreateLeague_seedTeamSnapshot",
          payload: { teamID: teamRef.id },
          error: e
        });
      }

      // 5) Update the user doc with league/team mapping and per-league roles map
      const userRef = doc(db, "users", currentUser.uid);
      try {
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const data = userSnap.data();
          const updatedLeagueIDs = data.leagueIDs
            ? [...data.leagueIDs, leagueRef.id]
            : [leagueRef.id];
          const updatedTeams = { ...(data.teams || {}), [leagueRef.id]: teamRef.id };
          // Merge existing roles map with new league role
          const prevRoles = data.roles && typeof data.roles === "object" ? data.roles : {};
          const updatedRoles = { ...prevRoles, [leagueRef.id]: "admin" };
          await updateDoc(userRef, {
            leagueIDs: updatedLeagueIDs,
            teams: updatedTeams,
            currentLeagueID: leagueRef.id,
            roles: updatedRoles,
          });
        } else {
          await setDoc(userRef, {
            email: currentUser.email,
            displayName: currentUser.email,
            leagueIDs: [leagueRef.id],
            teams: { [leagueRef.id]: teamRef.id },
            currentLeagueID: leagueRef.id,
            roles: { [leagueRef.id]: "admin" },
          });
        }
      } catch (e) {
        await logError({
          leagueID: leagueRef.id,
          userID: currentUser?.uid,
          action: "handleCreateLeague_userDoc",
          payload: {},
          error: e
        });
      }

      try {
        await logEvent({
          eventType: "league_created",
          leagueID: leagueRef.id,
          userID: currentUser.uid,
          userName: resolvedUserName,
          role: "admin",
          source: "create_league",
          metadata: {
            leagueType: String(leagueType),
            chipsEnabled: !!chipsEnabled,
            matchDay,
            timeOfDay,
            initialDeadlineSet: !!deadline,
            playersCount: Array.isArray(players) ? players.filter((p) => p?.name?.trim()).length : 0,
          },
        });
      } catch (e) {
        console.warn("[analytics] league_created failed", e);
      }

      try {
        const functions = getFunctions(undefined, "us-central1");
        const sendLeagueCreatedEmail = httpsCallable(
          functions,
          "sendLeagueCreatedEmail"
        );

        await sendLeagueCreatedEmail({
          email: currentUser.email,
          leagueName: leagueName.trim(),
          leagueCode: generatedLeagueCode,
        });
      } catch (emailErr) {
        console.error("League created email failed:", emailErr);
      }

      clearDraft(currentUser.uid);
      setSuccessModal({ leagueName: leagueName.trim(), leagueCode: generatedLeagueCode, leagueID: leagueRef.id });
    } catch (err) {
      try {
        await logEvent({
          eventType: "page_error_caught",
          leagueID: "",
          userID: currentUser?.uid || "",
          userName: resolvedUserName,
          role: "admin",
          source: "create_league",
          metadata: {
            action: "handleCreateLeague_main",
            message: err?.message || String(err),
          },
        });
      } catch (e) {
        console.warn("[analytics] page_error_caught failed", e);
      }
      console.error("League creation failed:", err);
      await logError({
        leagueID: "N/A",
        userID: currentUser?.uid,
        action: "handleCreateLeague_main",
        payload: { leagueName, teamName, leagueType },
        error: err
      });
      showToast("Failed to create league. Please try again.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const deadlineDisplay = React.useMemo(() => {
    if (!deadline) return "";
    const d = new Date(deadline);
    if (Number.isNaN(d.getTime())) return deadline;
    return d.toLocaleString(undefined, {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  }, [deadline]);

  if (!currentUser) {
    return (
      <Layout>
        <PageLoader />
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Top controls */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 10, marginBottom: 16 }}>
        <Button onClick={() => navigate("/home")}>← Back to My Leagues</Button>
        <Button
          onClick={() => {
            if (window.confirm("Discard your current draft?")) {
              clearDraft(currentUser.uid);
              setStep(1);
              setLeagueName("");
              setTeamName("");
              setLeagueType("5");
              setPlayers(buildEmptyPlayers(getLeagueConfig("5")?.minPlayers ?? 1));
              const config = getLeagueConfig("5");
              setChipsEnabled(config.chipsEnabled);
              setMaxmaxFreeTransfers(config?.maxFreeTransfers ?? 2);
              setTransferPenaltyPoints(config?.transferPenaltyPoints ?? 5);
              setDeadline("");
              setMatchDay("");
              setTimeOfDay("");
              setIsMatchDaySelected(false);
              setIsTimeOfDaySelected(false);
            }
          }}
        >
          Discard Draft
        </Button>
        <Button onClick={() => navigate("/faq")}>FAQ</Button>
      </div>

      {/* Progress bar */}
      <div style={{ marginBottom: 16 }}>
        <div
          style={{
            height: 6,
            width: "100%",
            background: "#eee",
            borderRadius: 4,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              height: "100%",
              width: `${(step / 4) * 100}%`,
              background: "#ff2b6a",
              transition: "width 0.25s ease",
            }}
          />
        </div>
        <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>
          Step {step} of 4
        </div>
      </div>

      {/* STEP 1 */}
      {step === 1 && (
        <Panel style={{ maxWidth: 700 }}>
          <h2>Step 1 - Create a New League</h2>
          <input
            type="text"
            placeholder="League Name"
            value={leagueName}
            onChange={(e) => setLeagueName(e.target.value)}
            style={{ display: "block", margin: "12px 0", width: "100%" }}
          />
          <input
            type="text"
            placeholder="Your Team Name"
            value={teamName}
            onChange={(e) => setTeamName(e.target.value)}
            style={{ display: "block", margin: "12px 0", width: "100%" }}
          />
          <label style={{ display: "block", marginTop: 10 }}>League Type:</label>
          <Dropdown
            value={leagueType}
            onChange={(val) => {
              setLeagueType(val);
              const config = getLeagueConfig(val);
              setChipsEnabled(config.chipsEnabled);
            }}
            options={[
              { value: "5", label: "5-a-side" },
              { value: "7", label: "7-a-side" },
              { value: "9", label: "9-a-side" },
              { value: "11", label: "11-a-side" },
            ]}
            placeholder="Select League Type"
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />

          {/* New fields for league settings */}
          <div style={{ marginTop: 16 }}>
            <label style={{ display: "block", marginTop: 10 }}>Match Day:</label>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ width: "100%" }}>
                <Dropdown
                  value={matchDay}
                  onChange={(val) => {
                    setMatchDay(val);
                    setIsMatchDaySelected(!!val);
                  }}
                  options={daysOfWeek.map((d) => ({ value: d, label: d }))}
                  placeholder="Select a day"
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                />
              </div>
              {isMatchDaySelected && (
                <span style={{ fontSize: 12, color: "#666", whiteSpace: "nowrap" }}>
                  Adjustable later
                </span>
              )}
            </div>

            <label style={{ display: "block", marginTop: 10 }}>Match Time:</label>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ width: "100%" }}>
                <Dropdown
                  value={timeOfDay}
                  onChange={(val) => {
                    setTimeOfDay(val);
                    setIsTimeOfDaySelected(!!val);
                  }}
                  options={[
                    { value: "Daytime", label: "Daytime" },
                    { value: "Evening", label: "Evening" },
                  ]}
                  placeholder="Select time"
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                />
              </div>
              {isTimeOfDaySelected && (
                <span style={{ fontSize: 12, color: "#666", whiteSpace: "nowrap" }}>
                  Adjustable later
                </span>
              )}
            </div>

            <label style={{ display: "block", marginTop: 10 }}>Calculated Deadline (auto-set):</label>
            <div style={{ fontSize: 14, marginTop: 4, marginBottom: 6 }}>
              <strong>{deadlineDisplay || ""}</strong>
            </div>
            <div style={{ fontSize: 12, color: "#666", marginBottom: 8 }}>
              Based on Match Day and Match Time. Change to a specific kickoff time later in Manage League.
            </div>
          </div>

          <div style={{ marginTop: 16 }}>
            {showStep1Errors && (
              <div style={{ marginBottom: 10, fontSize: 13, color: "#b00020" }}>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>
                  To continue, please fix:
                </div>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {step1Errors.map((msg) => (
                    <li key={msg}>{msg}</li>
                  ))}
                </ul>
              </div>
            )}

            <Button variant="pink" disabled={!canProceedStep1} onClick={nextStep}>
              Next →
            </Button>
          </div>
        </Panel>
      )}

      {/* STEP 2 */}
      {step === 2 && (
        <Panel style={{ maxWidth: 700 }}>
          <h2>Step 2 — Add Players (min {requiredPlayerCount})</h2>
          <div style={{ fontSize: 13, color: "#666", marginBottom: 10 }}>
            We’ve opened {requiredPlayerCount} player slots to match your league size. Add more if needed.
          </div>

          {players.map((p, idx) => (
            <div key={p.id} style={{ display: "flex", gap: 8, margin: "6px 0" }}>
              <input
                type="text"
                placeholder={`Player ${idx + 1} Name`}
                value={p.name}
                onChange={(e) => handlePlayerField(idx, "name", e.target.value)}
                style={{ flex: 1 }}
              />
              <Button onClick={() => handleRemovePlayerRow(idx)}>
                Remove
              </Button>
            </div>
          ))}

          <div style={{ marginTop: 8 }}>
            <Button onClick={handleAddPlayerRow}>+ Add Player</Button>
          </div>

          <div style={{ marginTop: 16 }}>
            {showStep2Errors && (
              <div style={{ marginBottom: 10, fontSize: 13, color: "#b00020" }}>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>
                  To continue, please fix:
                </div>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {step2Errors.map((msg) => (
                    <li key={msg}>{msg}</li>
                  ))}
                </ul>
              </div>
            )}

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <Button onClick={prevStep}>← Back</Button>
              <Button variant="pink" disabled={!canProceedStep2} onClick={nextStep}>
                Next →
              </Button>
            </div>
          </div>
        </Panel>
      )}

      {/* STEP 3 */}
      {step === 3 && (
        <Panel style={{ maxWidth: 700 }}>
          <h2>Step 3 — Choose Player Positions & Ratings</h2>

          <PlayerRatingsStep
            players={players}
            setPlayers={setPlayers}
            readOnly={false}
            hideNav
          />

          <div style={{ marginTop: 12 }}>
            {showStep3Errors && (
              <div style={{ marginBottom: 10, fontSize: 13, color: "#b00020" }}>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>
                  To continue, please fix:
                </div>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {step3Errors.map((msg) => (
                    <li key={msg}>{msg}</li>
                  ))}
                </ul>
              </div>
            )}
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <Button onClick={prevStep}>← Back</Button>
              <Button variant="pink" onClick={nextStep} disabled={!canProceedStep3}>
                Next →
              </Button>
            </div>
          </div>
        </Panel>
      )}

      {/* STEP 4 */}
      {step === 4 && (
        <Panel style={{ maxWidth: 700 }}>
          <h2>Step 4 — Confirm League Settings</h2>

          {/* League details display */}
          <div style={{ marginTop: 12, marginBottom: 6 }}>
            <p>
              <strong >League Name:  {leagueName}</strong>
            </p>
            {/* Add Players subsection header */}
            <div style={{ marginTop: 8, marginBottom: 6, textDecoration: "underline" }}>
              <strong> Players</strong>
            </div>
            <div style={{ marginTop: 8, marginBottom: 16 }}>
              {players.map((p) => {
                const pos = p.position || "MID";
                const rating = typeof p.rating === "number" ? p.rating : 3;
                const price =
                  typeof p.price === "number" ? p.price : computePrice(pos, rating);
                return (
                  <div
                    key={p.id}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "clamp(90px, 35vw, 200px) 1fr",
                      columnGap: 6,
                      marginBottom: 6,
                      lineHeight: 1.6,
                      alignItems: "center",
                    }}
                  >
                    <strong
                      title={p.name}
                      style={{
                        textTransform: "capitalize",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {p.name}
                    </strong>
                    <span
                      style={{
                        color: "#555",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {pos} • Rating {rating} • ${price.toFixed(1)}m
                    </span>
                  </div>
                );
              })}
            </div>
            {/* League settings header */}
            <div style={{ marginTop: 12, marginBottom: 6 }}>
              <strong style={{ textDecoration: "underline" }}>League settings</strong>
            </div>
            <p>
              <strong>Your Team Name:</strong> {teamName}
            </p>
            <p>
              <strong>League Type:</strong> {leagueType}-a-side
            </p>
            <p>
              <strong>Match Day:</strong> {matchDay}
            </p>
            <p>
              <strong>Match Time:</strong> {timeOfDay}
            </p>
            <p>
              <strong>Initial Deadline:  {deadlineDisplay || ""}</strong>
            </p>
            <p>
              <strong>Max Free Transfers:</strong> {leagueConfig.maxFreeTransfers ?? maxmaxFreeTransfers} (For {leagueType}-a-side)
            </p>
            <p>
              <strong>Transfer Penalty Points:</strong> {leagueConfig.transferPenaltyPoints ?? transferPenaltyPoints}
            </p>
          </div>

          {/* Chips toggle inline (single-line) */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "1rem 0" }}>
            <strong>Chips:</strong>
            <Dropdown
              value={chipsEnabled ? "yes" : "no"}
              onChange={(val) => setChipsEnabled(val === "yes")}
              options={[
                { value: "no", label: "Disabled" },
                { value: "yes", label: "Enabled" },
              ]}
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            />
          </div>
          <div style={{ fontSize: 12, color: "#666", marginBottom: 8 }}>
            When enabled: Goal Boost, Triple Captain, Fortress.
            <br />
            You can change deadline in Manage League.
          </div>

          <div style={{ marginTop: 12 }}>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <Button onClick={prevStep}>← Back</Button>
              <Button
                variant="pink" 
                onClick={handleCreateLeague}
                disabled={submitting || !canProceedStep3}
              >
                {submitting ? "Creating…" : "Create League"}
              </Button>
            </div>
          </div>
        </Panel>
      )}
      <LoadingOverlay show={submitting} message="Creating your league…" />

      {/* Post-creation success modal */}
      {successModal && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: "rgba(0,0,0,0.55)",
          display: "flex", justifyContent: "center", alignItems: "center",
          zIndex: 1200, padding: "16px",
        }}>
          <div style={{
            backgroundColor: "#fff", borderRadius: "12px",
            padding: "28px 24px", maxWidth: "420px", width: "100%",
            textAlign: "center", boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
          }}>
            <div style={{ fontSize: "2rem", marginBottom: 8 }}>🎉</div>
            <h2 style={{ margin: "0 0 4px", fontSize: "1.4rem", fontWeight: 800 }}>League Created!</h2>
            <p style={{ margin: "0 0 20px", color: "#555", fontSize: "0.95rem" }}>
              {successModal.leagueName}
            </p>

            {/* League code box — same style as ManageLeague */}
            <div style={{ background: "#f6f6f6", borderRadius: "8px", padding: "12px 16px", marginBottom: "16px" }}>
              <div style={{ fontSize: "0.8rem", color: "#777", marginBottom: 4 }}>League Code</div>
              <div style={{ fontWeight: 800, fontSize: "1.6rem", letterSpacing: "0.12em", color: "#222" }}>
                {successModal.leagueCode}
              </div>
              <div style={{ fontSize: "0.78rem", color: "#999", marginTop: 4 }}>
                Share this with your teammates so they can join
              </div>
            </div>

            {/* Copy Code */}
            <button
              onClick={() => {
                navigator.clipboard.writeText(successModal.leagueCode).then(() => {
                  setCopiedCode(true);
                  setTimeout(() => setCopiedCode(false), 2000);
                });
              }}
              style={{
                width: "100%", padding: "10px", marginBottom: "8px",
                borderRadius: "8px", border: "1px solid #e0e0e0",
                background: copiedCode ? "#e8f5e9" : "#fff",
                color: copiedCode ? "#2e7d32" : "#333",
                fontWeight: 600, fontSize: "0.92rem", cursor: "pointer",
              }}
            >
              {copiedCode ? "Copied! ✓" : "Copy Code"}
            </button>

            {/* Copy Invite Message */}
            <button
              onClick={() => {
                const msg = `Join my ClubWeek league! Code: ${successModal.leagueCode} — download at clubweek.co`;
                navigator.clipboard.writeText(msg).then(() => {
                  setCopiedInvite(true);
                  setTimeout(() => setCopiedInvite(false), 2000);
                });
              }}
              style={{
                width: "100%", padding: "10px", marginBottom: "16px",
                borderRadius: "8px", border: "1px solid #e0e0e0",
                background: copiedInvite ? "#e8f5e9" : "#fff",
                color: copiedInvite ? "#2e7d32" : "#333",
                fontWeight: 600, fontSize: "0.92rem", cursor: "pointer",
              }}
            >
              {copiedInvite ? "Copied! ✓" : "Copy Invite Message"}
            </button>

            {/* Go to My League */}
            <button
              onClick={() => {
                setSuccessModal(null);
                navigate(`/pickteam/${successModal.leagueID}`);
              }}
              style={{
                width: "100%", padding: "13px",
                borderRadius: "8px", border: "none",
                background: "#50B4C8", color: "#fff",
                fontWeight: 700, fontSize: "1rem", cursor: "pointer",
              }}
            >
              Go to My League →
            </button>
          </div>
        </div>
      )}
    </Layout>
  );
}